import type { ResourceSpecifier } from '@gonfalon/resource-specifiers/ast';
import type { RoleAttribute } from '@gonfalon/resource-specifiers/constants';
import { isEnvironmentScopedResource } from '@gonfalon/resource-specifiers/isEnvironmentScopedResource';
import { isOptionalViewScopedResource } from '@gonfalon/resource-specifiers/isOptionalViewScopedResource';
import { isProjectScopedResource } from '@gonfalon/resource-specifiers/isProjectScopedResource';
import { parse as resourceSpecifierFromString } from '@gonfalon/resource-specifiers/parser';

import type { Statement } from '../internal/types';

import { uniqObjArrayByMultipleProperties } from './uniqObjArrayByMultipleProperties';

const getRoleAttributes = (specifier: ResourceSpecifier, combineByType = true): RoleAttribute[] => {
  const roleAttributes: RoleAttribute[] = [];

  if (specifier.type === 'acct' || specifier.type === 'ip-allowlist') {
    return roleAttributes;
  }

  // creating one attribute object per resource type - used for RoleBuilder form state
  if (combineByType) {
    if (
      specifier.roleAttribute ||
      specifier.tagsRoleAttribute ||
      (isOptionalViewScopedResource(specifier) && specifier.viewsRoleAttribute)
    ) {
      roleAttributes.push({
        type: specifier.type,
        attribute: specifier.roleAttribute,
        tagsRoleAttribute: specifier.tagsRoleAttribute,
        viewsRoleAttribute: isOptionalViewScopedResource(specifier) ? specifier.viewsRoleAttribute : undefined,
      });
    }
  }

  // one RoleAttribute object per resourceType and attribute - used when parsing role attributes to assign access and rendering in teams/members tables
  if (!combineByType) {
    if (specifier.roleAttribute) {
      roleAttributes.push({
        type: specifier.type,
        attribute: specifier.roleAttribute,
      });
    }

    if (specifier.tagsRoleAttribute) {
      roleAttributes.push({
        type: specifier.type,
        tagsRoleAttribute: specifier.tagsRoleAttribute,
      });
    }

    if (isOptionalViewScopedResource(specifier) && specifier.viewsRoleAttribute) {
      roleAttributes.push({
        type: specifier.type,
        viewsRoleAttribute: specifier.viewsRoleAttribute,
      });
    }
  }

  if (isProjectScopedResource(specifier)) {
    roleAttributes.push(...getRoleAttributes(specifier.project, combineByType));
  }

  if (isEnvironmentScopedResource(specifier)) {
    roleAttributes.push(...getRoleAttributes(specifier.environment, combineByType));
  }

  return roleAttributes;
};

const getRoleAttributesFromSpecifiers = (specifiers: ResourceSpecifier[], combineByType = true) => {
  const allRoleAttributes = specifiers.flatMap((specifier) => getRoleAttributes(specifier, combineByType));
  const deduplicated = uniqObjArrayByMultipleProperties(allRoleAttributes, [
    'type',
    'attribute',
    'tagsRoleAttribute',
    'viewsRoleAttribute',
  ]);
  return deduplicated;
};

export const getRoleAttributesFromPolicy = (policy: Statement[], combineByType = true) => {
  const allResources = policy.flatMap((statement) => [
    ...(statement.resources || []),
    ...(statement.notResources || []),
  ]);
  const specifiers = allResources.reduce<ResourceSpecifier[]>((acc, resource) => {
    const { ok, val } = resourceSpecifierFromString(resource);
    if (ok) {
      acc.push(val);
    }
    return acc;
  }, []);

  return getRoleAttributesFromSpecifiers(specifiers, combineByType);
};
